import { GetTableListParams } from "./request";

/**
 * Deliverables — a named, self-contained HTML document the advisor generates
 * from a plain-English prompt plus uploaded source material.
 *
 * Three services back this feature and their contracts live together here:
 * - focalmonolith owns the persisted records (`Deliverable`, `DeliverableFigure`,
 *   `DeliverableTemplate`) and their request DTOs.
 * - document-service owns upload intake, logo preparation and PDF rendering.
 * - agent-service owns the streaming build and chat editing.
 *
 * The chat side is a conversation, not a command line. A turn may pull Focal
 * data, answer a question, ask one back, refuse a request or propose a change
 * and wait to be confirmed — so a turn can end without producing a document.
 * `DeliverableTurnOutcome` is what says which of those happened; a document
 * revision is only one of the possible endings, signalled by a `complete`
 * frame. Every field this vocabulary added is optional, because agent-service
 * may deploy after the app: a stream that only ever emits the old frames, and a
 * stored turn that only carries `status`, must keep working unchanged.
 *
 * Uploaded source *files* are never persisted: nothing in these types carries a
 * blob key, storage handle or attachment id for a statement. What is persisted
 * is the text document-service extracted from them (`DeliverableSourceSummary` /
 * `DeliverableSourceRecord`) plus the prepared branding (`DeliverableAsset`), so
 * a saved document can be edited against, and rebuilt from, the corpus it was
 * built out of.
 */

export type DeliverableGenerationKind = "BUILD" | "EDIT" | "REDESIGN";

export type DeliverableStatus =
  "GENERATING" | "COMPLETE" | "FAILED" | "FAILED_VALIDATION";

export type ArtifactBearingDeliverableStatus = Extract<
  DeliverableStatus,
  "COMPLETE" | "FAILED_VALIDATION"
>;

export type NoArtifactDeliverableStatus = Extract<
  DeliverableStatus,
  "GENERATING" | "FAILED"
>;

export type KnownDeliverableBuildStage =
  "queued" | "analyzing" | "designing" | "rendering" | "finalizing";

export type DeliverableBuildStage =
  KnownDeliverableBuildStage | (string & Record<never, never>);

export type DeliverableFailureReason =
  | "AGENT_LIMIT_REACHED"
  | "DOCUMENT_SERVICE_UNREACHABLE"
  | "INSPECTION_FAILED"
  | "VALIDATION_FAILED"
  | "UNKNOWN"
  | (string & Record<never, never>);

export interface DeliverableBranding {
  colors: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
}

/** `GET /deliverable/branding/defaults` — firm-level composer defaults. */
export interface DeliverableBrandingDefaults {
  colors: {
    primary: string | null;
    secondary: string | null;
    accent: string | null;
  };
  logoFileName: string | null;
}

export type DeliverableArtifactMetadata = Record<string, unknown>;

export type JsonValue =
  string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export type DeliverableFailureDetail = JsonValue;

/**
 * Where a number in the document came from. Decided by agent-service from the
 * filenames actually uploaded, never from what the model claims.
 * - `SOURCED` — the `sourceRef` names an uploaded file; it is `<filename> · p.<n>`.
 * - `UNSOURCED` — everything else, including a number the agent calculated from
 *   other figures. `sourceRef` may still describe where it came from.
 */
export type FigureOrigin = "SOURCED" | "UNSOURCED";

/** A persisted figure record. `value` is the formatted display string. */
export interface DeliverableFigure {
  id: string;
  deliverableId: string;
  label: string;
  value: string;
  /** `null` when the figure is unsourced. */
  sourceRef: string | null;
  origin: FigureOrigin;
  order: number;
}

/**
 * A figure as the agent emits it and as the client sends it on create /
 * figure-sync — no id yet, because the backend owns identity.
 */
export interface DeliverableFigureInput {
  label: string;
  value: string;
  sourceRef: string | null;
  origin: FigureOrigin;
  order?: number;
}

/** A lightweight client reference; the client is optional context, not a folder. */
export interface DeliverableClientRef {
  id: string;
  firstname: string;
  lastname: string;
}

/** A lightweight user reference used for owner / reviewer attribution. */
export interface DeliverableUserRef {
  id: string;
  firstname: string;
  lastname: string;
  email?: string;
}

interface DeliverableBase {
  id: string;
  firmId: string;
  ownerUserId: string;
  name: string;
  /** The prompt that produced the document. */
  promptText: string;
  /** Optional extra context for the agent only — never a filing category. */
  clientIds?: string[];
  clientResolution?: DeliverableClientResolution[];
  focalContext?: DeliverableFocalContext;
  clientId?: string | null;
  /** Denormalized so the pill survives a template rename or delete. */
  templateName?: string | null;
  /** Null for the three built-in defaults, which have no stored row. */
  templateId?: string | null;
  branding: DeliverableBranding;
  /**
   * Server-side build lifecycle. Older complete rows may omit it while the
   * coordinated backend deploy rolls forward; absence should be treated as a
   * complete artifact by legacy call sites.
   */
  status?: DeliverableStatus;
  /** The current coarse build phase, present while `status` is `GENERATING`. */
  buildStage?: DeliverableBuildStage | null;
  /** Stable machine-readable failure reason for terminal non-complete rows. */
  failureReason?: DeliverableFailureReason | null;
  /** Short user-inspectable JSON detail, already bounded by the backend. */
  failureDetail?: DeliverableFailureDetail | null;
  createdAt: number;
  updatedAt: number;
  /**
   * The stored corpus **without its text** — see `DeliverableSourceSummary`.
   * Returned by `GET /deliverable/:id`; use `getDeliverableSources` when the
   * text itself is needed.
   */
  sources?: DeliverableSourceSummary[];
  /** The advisor's branding, in full. Returned by `GET /deliverable/:id`. */
  assets?: DeliverableAsset[];
  client?: DeliverableClientRef | null;
  ownerUser?: DeliverableUserRef;
}

export interface ArtifactBearingDeliverable extends DeliverableBase {
  status?: ArtifactBearingDeliverableStatus;
  /** The complete self-contained document: assets as data URIs, charts inline SVG. */
  html: string;
  currentRevisionId: string;
  generationKind: DeliverableGenerationKind;
  artifactMetadata: DeliverableArtifactMetadata;
  /** Returned by `GET /deliverable/:id`. */
  figures?: DeliverableFigure[];
}

export interface NoArtifactDeliverable extends DeliverableBase {
  status: NoArtifactDeliverableStatus;
  html?: null;
  currentRevisionId: null;
  generationKind?: null;
  artifactMetadata?: null;
  figures?: undefined;
}

export type Deliverable = ArtifactBearingDeliverable | NoArtifactDeliverable;

export const hasDeliverableArtifact = (
  deliverable: Deliverable,
): deliverable is ArtifactBearingDeliverable =>
  Boolean(deliverable.html && deliverable.currentRevisionId);

/** Where a stored source came from. */
export type DeliverableSourceOrigin =
  "BUILD" | "CHAT_ATTACHMENT" | "FOCAL_LOOKUP";

/**
 * One stored source document, without its extracted text.
 *
 * This is what every deliverable read carries. The text is deliberately absent:
 * a corpus runs to megabytes and the viewer never needs it, so pulling it on
 * every navigation would be slow and would hold source material in the memory
 * of read-only recipients who never edit.
 *
 * `filename` is a label — nothing resolves it, because the file itself was never
 * stored.
 */
export interface DeliverableSourceSummary {
  id: string;
  deliverableId: string;
  filename: string;
  /** The type intake resolved from magic bytes, not the declared one. */
  mimeType: string;
  /** Whether intake could read the file. */
  ok: boolean;
  /** Why it could not. Absent when `ok`. */
  rejectionReason?: string;
  pageCount?: number;
  sheetCount?: number;
  estimatedTokens?: number;
  /** Characters of extracted text held for this source. */
  charCount: number;
  /**
   * Whether the advisor ticked this source for the build. `false` rows are kept
   * on purpose: an excluded document is still context a later edit may want.
   */
  includedInBuild: boolean;
  origin: DeliverableSourceOrigin;
  order: number;
  createdAt: number;
  updatedAt: number;
}

/**
 * A stored source **with** its text, from `GET /deliverable/:id/source`.
 *
 * The expensive read. Fetch it only when something is about to use the corpus —
 * a chat turn or a regeneration.
 */
export type DeliverableSourceRecord = DeliverableSourceSummary & {
  extractedText: string;
};

/** Body of `PUT`/`POST /deliverable/:id/source`. */
export interface DeliverableSourceInput {
  filename: string;
  mimeType: string;
  extractedText: string;
  ok?: boolean;
  rejectionReason?: string;
  pageCount?: number;
  sheetCount?: number;
  estimatedTokens?: number;
  includedInBuild: boolean;
  origin: DeliverableSourceOrigin;
  order: number;
  /** Where a Focal-read source came from; both omitted for an upload. */
  conversationId?: string;
  crmNoteIds?: string[];
}

export interface SyncDeliverableSourcesPayload {
  sources: DeliverableSourceInput[];
}

/** Body of `PUT /deliverable/:id/asset`. `order` is the monolith's addition. */
export type DeliverableAssetInput = DeliverableAsset & { order: number };

export interface SyncDeliverableAssetsPayload {
  assets: DeliverableAssetInput[];
}

/**
 * How the list surfaces a row's sharing state ("Private", "2 recipients").
 * Mirrors the monolith's `DeliverableSharingSummaryDto`, which counts
 * permission grants other than the owner's own always-present WRITE grant.
 * Group grants carry no user id and are counted as recipients too, so the
 * summary cannot tell a person from a group — a row that needs that
 * distinction has to fall back to the entity-permission endpoint.
 *
 * Only `GET /deliverable` (the list) populates it; single-row reads leave it
 * undefined and the UI falls back to the entity-permission endpoint.
 */
export interface DeliverableSharingSummary {
  isShared: boolean;
  /** Permission grants excluding the owner's own grant. */
  recipientCount: number;
}

type DeliverableListExtras = {
  figureCount?: number;
  sharing?: DeliverableSharingSummary;
};

/**
 * A list row. `html` is omitted because the list never renders the document.
 * Generating and no-artifact failed rows still have a row id, name, lifecycle
 * status and a null current revision.
 */
export type DeliverableListItem =
  | (Omit<ArtifactBearingDeliverable, "html" | "figures"> &
      DeliverableListExtras)
  | (Omit<NoArtifactDeliverable, "html" | "figures"> & DeliverableListExtras);

/** `mine` = owned by the acting user; `shared` = shared with them. */
export type DeliverableListScope = "mine" | "shared";

export type DeliverableListParams = Partial<GetTableListParams> & {
  scope?: DeliverableListScope;
};

/** Body of `POST /deliverable`, creating a server-owned `GENERATING` row. */
export interface CreateDeliverablePayload {
  status: "GENERATING";
  name: string;
  promptText: string;
  /**
   * Focus / exclusion guidance, sent only when the advisor wrote some. Kept out
   * of `promptText` because the build prompts read that as the document's
   * section contract, so an exclusion folded into it reads as a section
   * instruction — and because the edit surface never receives `promptText`,
   * which is what let a later chat turn reintroduce excluded material.
   */
  focusInstructions?: string;
  /** Every client this document is about. The server reads the list from here. */
  clientIds?: string[];
  clientResolution?: DeliverableClientResolution[];
  /** @deprecated Single-client legacy fallback; `clientIds` wins when present. */
  clientId?: string | null;
  templateName?: string | null;
  templateId?: string | null;
  branding: DeliverableBranding;
  sources: DeliverableSourceInput[];
  assets?: DeliverableAssetInput[];
  focalContext?: DeliverableFocalContextInput;
}

/** @deprecated Retained only for the pre-async browser build flow. */
export interface LegacyCreateDeliverablePayload {
  name: string;
  promptText: string;
  clientId?: string;
  branding: DeliverableBranding;
  initialRevision: DeliverableRevisionPayload;
}

export interface DeliverableRevisionPayload {
  html: string;
  artifactMetadata: DeliverableArtifactMetadata;
  generationKind: DeliverableGenerationKind;
  figures: DeliverableFigureInput[];
}

export type CreateDeliverableRequestPayload =
  CreateDeliverablePayload | LegacyCreateDeliverablePayload;

/** Body of `PATCH /deliverable/:id` — metadata only. */
export interface UpdateDeliverablePayload {
  name?: string;
}

/**
 * Body of `PUT /deliverable/:id/figure` — replaces the whole set after every
 * chat edit.
 */
export interface SyncFiguresPayload {
  figures: DeliverableFigureInput[];
}

/**
 * A saved prompt, and nothing else — no attachments, no branding assets. Unlike
 * a deliverable, a template is authored rather than built from anything, so
 * there is no corpus for it to carry.
 */
export interface DeliverableTemplate {
  id: string;
  firmId: string;
  ownerUserId: string;
  name: string;
  /** One-liner shown in the picker. */
  description?: string | null;
  promptText: string;
  createdAt: number;
  updatedAt: number;
  ownerUser?: DeliverableUserRef;
}

export interface CreateDeliverableTemplatePayload {
  name: string;
  description?: string;
  promptText: string;
}

export interface UpdateDeliverableTemplatePayload {
  name?: string;
  description?: string;
  promptText?: string;
}

/* -------------------------------------------------------------------------- */
/* document-service contracts                                                 */
/* -------------------------------------------------------------------------- */

/** Why a source file cannot be used. `ok: false` always carries one of these. */
export type InspectSourceRejectionReason =
  "PASSWORD_PROTECTED" | "NO_EXTRACTABLE_TEXT" | "UNSUPPORTED_TYPE" | "CORRUPT";

/**
 * One entry of the `POST /deliverable/inspect-sources` response.
 * `estimatedTokens` powers the context meter; `perPageTokens` (PDFs only)
 * powers page-range trimming.
 */
export interface InspectSourceResult {
  filename: string;
  mimeType: string;
  ok: boolean;
  reason?: InspectSourceRejectionReason;
  pageCount?: number;
  sheetCount?: number;
  estimatedTokens: number;
  /** Token count per page, index 0 = page 1. Present for PDFs. */
  perPageTokens?: number[];
  extractedText?: string;
}

/** Response of `POST /deliverable/prepare-logo`. Embedded in the document forever. */
export interface PrepareLogoResult {
  dataUri: string;
  width: number;
  height: number;
  bytes: number;
}

/** Body of `POST /deliverable/render-pdf`. Responds with an `application/pdf` stream. */
export interface RenderPdfRequest {
  html: string;
  footerText: string;
  /** True while the deliverable is a draft — stamps `DRAFT — NOT REVIEWED`. */
  watermark: boolean;
  title: string;
}

/* -------------------------------------------------------------------------- */
/* agent-service contracts                                                    */
/* -------------------------------------------------------------------------- */

/**
 * One source as agent-service receives it. agent-service never sees the file
 * bytes — document-service extracted the text at upload time and the whole
 * verdict is forwarded here, so `mimeType` and `extractedText` are required by
 * its `SourceFile` schema, not optional extras.
 */
export interface DeliverableBuildSource {
  filename: string;
  mimeType: string;
  extractedText: string;
  pageCount?: number;
  sheetCount?: number;
  estimatedTokens?: number;
}

/**
 * A source agent-service read out of Focal, as the `sources` frame reports it.
 *
 * Separate from `DeliverableBuildSource` because that shape is what the app
 * *sends* as uploads, and `BuildRequest` forbids unknown keys.
 */
export interface DeliverableResolvedSource extends DeliverableBuildSource {
  conversationId?: string;
  /** In resolved order. Empty for a meeting. */
  crmNoteIds: string[];
}

/**
 * One advisor-supplied asset the model may place in the document.
 *
 * `role` is what the model is shown — it decides whether the asset belongs, where
 * it goes and how big it is, so nothing here carries a display size. The coming
 * branding work adds entries to this list rather than fields to this shape.
 */
export interface DeliverableAsset {
  /** Stable id the model references in `data-asset`. */
  id: string;
  /** What this asset is, in words, e.g. "Advisor's logo". */
  role: string;
  /** The asset itself; agent-service rejects anything but a `data:image/*` URI. */
  dataUri: string;
  /** Intrinsic pixel size of the source image, not a display size. */
  width?: number;
  height?: number;
}

/**
 * The Focal data the advisor explicitly picked on the new-deliverable page.
 *
 * Only ids and flags travel: the monolith resolves each record into corpus
 * text at kickoff with the caller's own token, the same way the build already
 * reads branding and assets. There is no "everything recent" option on purpose
 * — the selection is the advisor's, so what lands in the document is something
 * they chose. That is also why notes are a list of ids rather than a flag: the
 * flag meant "every recent note", which is the opposite of a choice.
 *
 * The two client flags are the exception, and deliberately so: a client's CRM
 * record and extracted meeting data are one thing each, not a list to pick
 * from. Both are ignored by the server unless a client is attached.
 */
export interface DeliverableClientResolution {
  clientId: string;
  sourceType: "crm" | "clientData";
  status: "included" | "empty" | "unavailable";
}

/**
 * What the advisor picked, as the create body carries it.
 *
 * Deliberately missing `clientIds` and `clientResolution`: the attached clients
 * travel at the top level of the create payload, and the resolution outcomes are
 * the server's answer, not our request. The create DTO whitelists this shape
 * exactly and rejects anything else outright, so the two directions cannot share
 * one interface — sending `clientIds` in here is a 400.
 */
export interface DeliverableFocalContextInput {
  /** Conversations to read, in the order the advisor picked them. */
  conversationIds: string[];
  /** CRM notes to read, in the order the advisor picked them. */
  crmNoteIds: string[];
  /** Snapshot every attached client's CRM contact, custom fields and household. */
  includeCrmHousehold: boolean;
  /** Snapshot every attached client's extracted meeting data. */
  includeClientData: boolean;
}

/** What the server returns: the input above plus what it resolved against. */
export interface DeliverableFocalContext extends DeliverableFocalContextInput {
  clientIds?: string[];
  clientResolution?: DeliverableClientResolution[];
}

/** Body of `POST /deliverables/build` for the legacy browser-owned SSE build. */
export interface LegacyDeliverableBuildPayload {
  name: string;
  promptText: string;
  branding: DeliverableBranding;
  sources: DeliverableBuildSource[];
  assets?: DeliverableAsset[];
  /**
   * The attached Focal client. agent-service resolves the subject from it, and
   * it is what makes `focalContext` meaningful: a selection without a client
   * has nothing to resolve against.
   */
  clientId?: string;
  /**
   * The conversations and CRM notes the advisor picked, matching
   * agent-service's `BuildRequest.focal_context` (camel-cased on the wire).
   *
   * Omitted entirely when nothing was ticked. `BuildRequest` is `extra="forbid"`,
   * so an agent-service that has not shipped `focal_context` yet must never be
   * sent the key — and a build with no selection has nothing to say with it.
   */
  focalContext?: DeliverableFocalContext;
}

/** @deprecated Use `enqueueDeliverableBuild` with `deliverableId` instead. */
export type DeliverableBuildPayload = LegacyDeliverableBuildPayload;

export interface EnqueueDeliverableBuildPayload {
  deliverableId: string;
}

export interface EnqueueDeliverableBuildResponse {
  /**
   * Always empty: the client never reads the accepted response's body, because
   * waiting on it would block the enqueue behind a stream agent-service may not
   * have closed. Progress is keyed by `deliverableId`, not by a job id.
   */
  jobId: string;
}

/**
 * How one recorded edit turn ended.
 *
 * `pending` is not a transient the client writes — it is a row agent-service
 * opened and never closed, i.e. an edit whose run died. That is what tells a
 * reloaded page an edit was interrupted rather than refused.
 */
export type DeliverableTurnStatus = "pending" | "complete" | "failed";

/**
 * What a settled turn actually did. `status` only says whether the run finished;
 * this says what finishing meant.
 *
 * - `edit_applied` — the document changed and a revision was minted.
 * - `answered` — the agent replied in prose; the document is untouched.
 * - `clarification_requested` — the agent asked the advisor a question back.
 * - `blocked` — the agent declined the request. Not an error: never a toast.
 * - `confirmation_requested` — the agent described a change and is waiting to be
 *   told to make it; the turn carries a `proposal`.
 * - `failed` — the run broke. The document is untouched.
 */
export type DeliverableTurnOutcome =
  | "edit_applied"
  | "answered"
  | "clarification_requested"
  | "blocked"
  | "confirmation_requested"
  | "data_requested"
  | "edit_queued"
  | "failed";

/**
 * A change the agent has described but not made, stored on the turn that
 * proposed it so a reloaded page can still offer the confirmation.
 *
 * `parentRevisionId` is the revision the proposal was written against: if the
 * document has moved on since, the proposal is stale (`proposalState:
 * "superseded"`) rather than applicable.
 */
export interface DeliverableTurnProposal {
  proposalId: string;
  summary: string;
  expectedChanges: string[];
  parentRevisionId: string | null;
}

/**
 * One turn of a deliverable's edit conversation, as agent-service stores it.
 *
 * A turn does not necessarily answer with a document. `outcome` says how it
 * ended and `replyText` carries the assistant prose when the answer was words
 * rather than a revision — an `edit_applied` turn is normally silent, an
 * `answered` / `clarification_requested` / `blocked` turn is prose only. Both
 * are optional: a turn written by an agent-service that predates this
 * vocabulary has `status` alone, and the transcript falls back to it.
 */
export interface DataRequest {
  requestId: string;
  summary: string;
  question: string;
  status: "pending" | "running" | "complete" | "declined" | "failed";
  interruptId?: string;
}

export interface DeliverableChatPayload {
  deliverableId: string;
  message: string;
  clientTurnId: string;
  baseRevisionId: string;
  attachments?: { filename: string; charCount: number }[];
  turnId?: string;
  dataRequestId?: string;
  decision?: "confirm" | "decline";
}

export interface DeliverableConversationTurn {
  id: string;
  userId: string;
  /**
   * The id the client sent for the attempt that created this turn. How a viewer
   * finds its own row: request text is not an identity, since two people can
   * send the same wording at the same moment.
   */
  clientTurnId: string | null;
  requestText: string;
  status: DeliverableTurnStatus;
  /** Absent on a turn stored before the outcome vocabulary shipped. */
  outcome?: DeliverableTurnOutcome;
  /** The assistant's words for this turn, when it answered with prose. */
  replyText?: string | null;
  /** Present on a `confirmation_requested` turn. */
  proposal?: DeliverableTurnProposal | null;
  /**
   * Where the turn's proposal stands now. Set by the turns that came after it,
   * not by this one: `accepted` / `declined` once the advisor answered, and
   * `superseded` once the document moved past `proposal.parentRevisionId`.
   */
  proposalState?: "pending" | "accepted" | "declined" | "superseded";
  /** The outcome of the later turn that resolved this one's proposal. */
  resolvedByOutcome?: DeliverableTurnOutcome | null;
  dataRequest?: DataRequest | null;
  editStatus?:
    "queued" | "running" | "finalizing" | "succeeded" | "failed" | null;
  /** Position in the conversation; the cursor for paging older turns. */
  seq: number;
  createdAt: string;
}

/**
 * Assistant prose for a chat turn — the reply itself when the turn answers with
 * words instead of a document.
 *
 * `kind` says how to render it: `blocked` is an info note rather than a chat
 * bubble, `proposal` accompanies a `proposal` frame. It is optional because an
 * agent-service that predates the outcome vocabulary sends content only.
 */
export interface DeliverableMessageEvent {
  type: "message";
  content: string;
  kind?: "answer" | "question" | "blocked" | "proposal";
}

/**
 * The agent reached for Focal data (or another tool). Progress only — nothing is
 * persisted from these frames; they drive the live activity line and are safe to
 * drop.
 */
export interface DeliverableToolEvent {
  type: "tool";
  phase: "start" | "end";
  name: string;
  detail?: string;
}

/**
 * A change the agent wants confirmed before it makes it. The document is
 * untouched: nothing here is applied until the advisor replies.
 */
export interface DeliverableProposalEvent {
  type: "proposal";
  proposal: DeliverableTurnProposal;
}

/**
 * How the turn ended, stated by agent-service rather than inferred from which
 * frames happened to arrive. This is the explicit terminator: a stream that ends
 * with no `turn`, `complete`, `message` or `error` frame is a dropped
 * connection, not a settled turn.
 */
export interface DeliverableTurnEvent {
  type: "turn";
  outcome: DeliverableTurnOutcome;
  turnId?: string;
  seq?: number;
}

/** The fully assembled, self-contained document. */
export interface DeliverableCompleteEvent {
  type: "complete";
  html: string;
  figures: DeliverableFigureInput[];
  artifactMetadata: DeliverableArtifactMetadata;
}

/**
 * What the build resolved out of Focal, handed back so the client can store it.
 *
 * agent-service writes nothing down, so without this frame the meetings and
 * notes a document was built from would exist only inside that run — the
 * finished deliverable's source panel would list the uploads alone, and a later
 * edit would have no meeting text to work against.
 */
export interface DeliverableSourcesEvent {
  type: "sources";
  sources: DeliverableResolvedSource[];
}

export interface DeliverableStatusEvent {
  type: "status";
  stage: string;
}

export interface DeliverablePreviewEvent {
  type: "preview";
  html: string;
}

export interface DeliverableStreamErrorEvent {
  type: "error";
  message: string;
  /**
   * The upstream structured reason, passed through verbatim by agent-service.
   * Deliberately a plain `string` rather than a union: the service documents it
   * as a passthrough, so narrowing it here would silently drop reasons it adds.
   */
  reason?: string;
}

/**
 * Every frame the build and chat streams can emit. Consumers must treat an
 * unrecognised `type` as ignorable rather than fatal.
 */
export interface DeliverableTokenEvent {
  type: "token";
  content: string;
}

export interface DeliverableDataRequestedEvent {
  type: "data_requested";
  turnId: string;
  dataRequest: DataRequest;
}

export type DeliverableChatEvent =
  | DeliverableTokenEvent
  | DeliverableDataRequestedEvent
  | DeliverableMessageEvent
  | DeliverableProposalEvent
  | DeliverableTurnEvent
  | DeliverableStatusEvent
  | DeliverableStreamErrorEvent;

export type DeliverableStreamEvent =
  | DeliverableTokenEvent
  | DeliverableDataRequestedEvent
  | DeliverableMessageEvent
  | DeliverableToolEvent
  | DeliverableProposalEvent
  | DeliverableTurnEvent
  | DeliverableStatusEvent
  | DeliverablePreviewEvent
  | DeliverableCompleteEvent
  | DeliverableSourcesEvent
  | DeliverableStreamErrorEvent;
